import { fetchRecommendedPgProducts, fetchRecommendedTgProducts } from '~/features/catalog/article.server';
import { optionalProtectedResourceLoader } from '~/shell/utils/authUtil.server';
import { getSelectedLocation } from '~/shell/utils/locationUtil.server';
import { getGkAirTrackingConsentFromContext } from '~/shell/globalContext.ts';
import invariant from 'tiny-invariant';
import { useQuery } from '@tanstack/react-query';
import { useBasePath } from '~/common/hooks/useBasePath';
import { TgArea } from '~/common/types/area';

export const loader = optionalProtectedResourceLoader(async ({ params, request, loginInfo, t, api, url }) => {
  // Ohne Tracking-Consent keine Empfehlungen: GK AIR wird gar nicht kontaktiert und die
  // leere Liste blendet die Sektion aus (WEB-3666). GkAirConsentSync invalidiert die Query
  // bei Consent-Wechsel, wodurch die Sektion reaktiv erscheint/verschwindet.
  if (!getGkAirTrackingConsentFromContext()) {
    return Response.json({ articles: [] });
  }

  const isTgArea = params.area === TgArea;
  const articleNumber = params.articleNumber;

  invariant(articleNumber, 'Expected articleNumber');

  const hwgId = url.searchParams.get('hwgId');
  const hagrCode = url.searchParams.get('hagrCode');
  const agrCode = url.searchParams.get('agrCode');
  const category = { hwgId, hagrCode, agrCode };

  const actionExclude = loginInfo?.actionExclude ?? false;
  const hasIndividualOrderEndTimes = loginInfo?.hasIndividualOrderEndTimes ?? false;
  const ccId = await getSelectedLocation(request);

  const searchResponse = isTgArea
    ? await fetchRecommendedTgProducts(
        api,
        request.signal,
        hasIndividualOrderEndTimes,
        articleNumber,
        t,
        actionExclude,
        category
      )
    : await fetchRecommendedPgProducts(api, request.signal, articleNumber, ccId, t, category);

  const articles = searchResponse.articles.filter(article => article.articleNumber !== articleNumber);

  return Response.json({ articles });
});

export function useRecommendedArticles(articleNumber: string, hwgId?: number, hagrCode?: number, agrCode?: number) {
  const { path } = useBasePath();

  const fetchRecommendedArticles = async () => {
    const params = new URLSearchParams();
    if (hwgId != null) params.set('hwgId', hwgId.toString());
    if (hagrCode != null) params.set('hagrCode', hagrCode.toString());
    if (agrCode != null) params.set('agrCode', agrCode.toString());
    const query = params.toString();
    const querySuffix = query ? `?${query}` : '';
    const url = `${path}/resources/articles/${articleNumber}/recommendedArticles${querySuffix}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch recommended articles: ${response.statusText}`);
    }

    return response.json();
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['recommendedArticles', articleNumber],
    queryFn: fetchRecommendedArticles,
    enabled: !!articleNumber,
    staleTime: 5 * 60 * 1000
  });

  return {
    articles: data?.articles ?? [],
    isLoading,
    error
  };
}
